(() => {
  if (customElements.get("recently-viewed")) return;

  const safeJsonParse = (val, fallback) => {
    try {
      return JSON.parse(val);
    } catch {
      return fallback;
    }
  };

  class RecentlyViewed extends HTMLElement {
    constructor() {
      super();
      this._started = false;
      this._attempts = 0;
      this._io = null;
    }

    connectedCallback() {
      if (this._started) return;
      this._started = true;

      // If query is empty, do nothing
      const q = this.getSearchQuery();
      if (!q) return;

      // Prefer IO to avoid network contention with LCP
      const rootMargin = this.dataset.rootMargin || "800px 0px";

      if ("IntersectionObserver" in window) {
        this._io = new IntersectionObserver(
          (entries) => {
            const e = entries && entries[0];
            if (!e || !e.isIntersecting) return;
            this._io.disconnect();
            this._io = null;
            this.init(q);
          },
          { rootMargin }
        );
        this._io.observe(this);
      } else {
        // Fallback: small delay (still avoids racing the initial critical requests)
        setTimeout(() => this.init(q), 700);
      }
    }

    disconnectedCallback() {
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
    }

    async init(q) {
      const baseUrl = this.dataset.url;
      if (!baseUrl) return;

      try {
        const url = `${baseUrl}&q=${encodeURIComponent(q)}`;
        const res = await fetch(url, { credentials: "same-origin" });
        if (!res.ok) throw new Error(String(res.status));

        const html = await res.text();
        const tmpl = document.createElement("template");
        tmpl.innerHTML = html;

        const incoming = tmpl.content.querySelector("recently-viewed");
        if (!incoming || !incoming.hasChildNodes()) return;

        // Swap inner HTML
        this.innerHTML = incoming.innerHTML;

        // Re-init only what is needed; avoid global resize spam
        // Many themes listen to section:load to bind sliders.
        // Provide detail to help theme code scope work if it supports it.
        const detail = {
          sectionId: this.dataset.sectionId || null,
          section: this,
          source: "recently-viewed",
        };

        // Use rAF to ensure DOM is committed before listeners run
        requestAnimationFrame(() => {
          document.dispatchEvent(new CustomEvent("section:load", { detail }));
        });
      } catch (err) {
        this._attempts += 1;
        if (this._attempts >= 3) return;

        // Exponential-ish backoff
        const wait = 250 * this._attempts;
        setTimeout(() => {
          const q2 = this.getSearchQuery();
          if (!q2) return;
          this.init(q2);
        }, wait);
      }
    }

    getSearchQuery() {
      const raw = safeJsonParse(localStorage.getItem("cc-recently-viewed") || "[]", []);
      const items = Array.isArray(raw)
        ? raw.map((x) => String(x).trim()).filter(Boolean)
        : [];

      if (!items.length) return "";

      const ex = (this.dataset.exclude || "").trim();
      if (ex) {
        const idx = items.indexOf(ex);
        if (idx > -1) items.splice(idx, 1);
      }

      const limit = Math.max(1, Number(this.dataset.limit || 8));
      return items
        .slice(0, limit)
        .map((id) => `id:${id}`)
        .join(" OR ");
    }
  }

  customElements.define("recently-viewed", RecentlyViewed);
})();
